import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Html5Qrcode } from "html5-qrcode";
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { API_URL } from "./config";
import {
  getCampusFromToken,
} from "./commonfunctions/useAuthHelpers";

const WalletGatePage = () => {
  // ✅ Theme constants
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const BLUE = "#1565C0";

  const pageWrapSx = { p: 2, backgroundColor: BG, minHeight: "100vh" };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };
  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: BORDER_FOCUS,
    },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const codeBoxSx = {
    p: 1.5,
    borderRadius: 2,
    backgroundColor: "#0f172a",
    color: "#e5e7eb",
    fontFamily: "monospace",
    fontSize: "0.82rem",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    overflowX: "auto",
    border: "1px solid rgba(255,255,255,0.08)",
  };

  const noteBoxSx = {
    p: 1.5,
    borderRadius: 2,
    border: `1px solid ${BORDER}`,
    backgroundColor: "rgba(25,118,210,0.04)",
  };

  const [campusCode] = useState(getCampusFromToken());
  const [deviceCode, setDeviceCode] = useState(localStorage.getItem("walletGateDeviceCode") || "");
  const [deviceSecret, setDeviceSecret] = useState(localStorage.getItem("walletGateDeviceSecret") || "");
  const [qrToken, setQrToken] = useState("");
  const [scanDirection, setScanDirection] = useState("ENTRY");
  const [msg, setMsg] = useState({ type: "", text: "" });

  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [cameraError, setCameraError] = useState("");

  const html5QrRef = useRef(null);
  const lastScanRef = useRef("");
  const lastScanAtRef = useRef(0);
  const scannerDivId = "wallet-gate-html5-qrcode";

  const isSecure =
    typeof window !== "undefined" &&
    (window.isSecureContext || window.location.hostname === "localhost");

  const gateScanUrl = useMemo(() => {
    const base = String(API_URL || "").replace(/\/$/, "");
    return `${base}/api/gate/scan`;
  }, []);

  const thirdPartyCurl = useMemo(() => {
    return `curl -X POST "${gateScanUrl}" \\
  -H "Content-Type: application/json" \\
  -H "x-campus-code: ${campusCode || "?????"}" \\
  -H "x-device-code: ${deviceCode || "?????"}" \\
  -H "x-device-secret: ${deviceSecret || "?????"}" \\
  -d '{
    "ScanDirection": "${scanDirection || "ENTRY"}",
    "qrRaw": "Select QrValue From idcard_issued Where StudentKey=????? AND QrIsActive=1;"
  }'`;
  }, [gateScanUrl, campusCode, deviceCode, deviceSecret, scanDirection]);

  const thirdPartyExamplePayload = useMemo(() => {
    return `{
  "ScanDirection": "ENTRY",
  "qrRaw": "https://your-domain.com/idcard/verify/KIET-STU-2026-000123?n=abcdef123456"
}`;
  }, []);

  useEffect(() => {
    if (!cameraOpen) return;

    let cancelled = false;

    const bootScanner = async () => {
      try {
        const el = document.getElementById(scannerDivId);
        if (!el) throw new Error("Scanner container not rendered");

        const cameras = await Html5Qrcode.getCameras();
        if (!cameras || !cameras.length) {
          throw new Error("No camera found on this device");
        }

        let chosenCameraId = cameras[0].id;
        const rearLike = cameras.find((c) =>
          /back|rear|environment/i.test(`${c.label || ""}`)
        );
        if (rearLike) chosenCameraId = rearLike.id;

        const html5Qr = new Html5Qrcode(scannerDivId, { verbose: false });
        html5QrRef.current = html5Qr;

        await html5Qr.start(
          chosenCameraId,
          {
            fps: 10,
            qrbox: { width: 240, height: 240 },
            aspectRatio: 1.333334,
          },
          async (decodedText) => {
            await handleDetectedCode(decodedText);
          },
          () => {}
        );

        if (!cancelled) {
          setCameraLoading(false);
        }
      } catch (err) {
        console.error("Camera open failed:", err);
        if (!cancelled) {
          setCameraError(err?.message || "Failed to open camera");
          setCameraLoading(false);
        }
      }
    };

    const t = setTimeout(bootScanner, 50);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [cameraOpen]);

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  const saveDevice = () => {
    localStorage.setItem("walletGateDeviceCode", deviceCode);
    localStorage.setItem("walletGateDeviceSecret", deviceSecret);
    setMsg({ type: "success", text: "Gate device credentials saved locally" });
  };

  const scanGate = async (tokenOverride = null) => {
    try {
      const tokenToUse = String(tokenOverride ?? qrToken ?? "").trim();

      if (!tokenToUse) {
        setMsg({ type: "error", text: "Please scan or enter a QR value" });
        return;
      }

      const res = await axios.post(
        `${API_URL}/api/gate/scan`,
        {
          ScanDirection: scanDirection,
          qrRaw: tokenToUse,
        },
        {
          headers: {
            "x-campus-code": campusCode,
            "x-device-code": deviceCode,
            "x-device-secret": deviceSecret,
          },
        }
      );

      setQrToken(tokenToUse);
      setMsg({
        type: res.data?.decision === "ALLOW" ? "success" : "error",
        text:
          res.data?.reason ||
          (res.data?.decision === "ALLOW" ? "Access allowed" : "Access denied"),
      });
    } catch (err) {
      setMsg({
        type: "error",
        text: err?.response?.data?.error || "Gate scan failed",
      });
    }
  };

  const handleDetectedCode = async (rawValue) => {
    const value = String(rawValue || "").trim();
    if (!value) return;

    const now = Date.now();
    if (lastScanRef.current === value && now - lastScanAtRef.current < 2000) {
      return;
    }

    lastScanRef.current = value;
    lastScanAtRef.current = now;

    setQrToken(value);
    await scanGate(value);
    await stopCamera();
  };

  const stopCamera = async () => {
    try {
      if (html5QrRef.current) {
        const state = html5QrRef.current.getState?.();
        if (state === 2 || state === 3) {
          await html5QrRef.current.stop();
        }
        await html5QrRef.current.clear();
        html5QrRef.current = null;
      }
    } catch (err) {
      console.error("Failed to stop camera:", err);
    } finally {
      setCameraOpen(false);
      setCameraLoading(false);
    }
  };

  const startCamera = async () => {
    if (!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)) {
      setMsg({ type: "error", text: "Camera is not supported in this browser on this device" });
      return;
    }

    if (!(window.isSecureContext || window.location.hostname === "localhost")) {
      setMsg({ type: "error", text: "Camera access requires HTTPS or localhost" });
      return;
    }

    setCameraError("");
    setCameraLoading(true);
    setCameraOpen(true);
  };

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900 }}>
              Wallet Gate Scanner
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Validate QR for entry and exit
            </Typography>
          </Box>
        </Box>

        <Box sx={bodySx}>
          {!!msg.text && (
            <Snackbar
              open
              autoHideDuration={3000}
              onClose={() => setMsg({ type: "", text: "" })}
            >
              <Alert severity={msg.type || "info"} sx={{ borderRadius: 2 }}>
                {msg.text}
              </Alert>
            </Snackbar>
          )}

          <Stack spacing={2}>
            <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
              <Stack spacing={2}>
                <TextField
                  label="Campus Code"
                  value={campusCode || ""}
                  size="small"
                  fullWidth
                  disabled
                  sx={inputSx}
                />

                <TextField
                  label="Gate Device Code"
                  value={deviceCode}
                  onChange={(e) => setDeviceCode(e.target.value)}
                  size="small"
                  fullWidth
                  sx={inputSx}
                />

                <TextField
                  label="Gate Device Secret"
                  value={deviceSecret}
                  onChange={(e) => setDeviceSecret(e.target.value)}
                  size="small"
                  fullWidth
                  sx={inputSx}
                />

                <Button variant="contained" sx={primaryBtnSx} onClick={saveDevice}>
                  Save Device
                </Button>

                <TextField
                  label="Scanned QR Raw Value"
                  value={qrToken}
                  onChange={(e) => setQrToken(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      scanGate();
                    }
                  }}
                  size="small"
                  fullWidth
                  multiline
                  minRows={3}
                  sx={inputSx}
                />

                <TextField
                  select
                  label="Direction"
                  value={scanDirection}
                  onChange={(e) => setScanDirection(e.target.value)}
                  size="small"
                  fullWidth
                  sx={inputSx}
                >
                  <MenuItem value="ENTRY">ENTRY</MenuItem>
                  <MenuItem value="EXIT">EXIT</MenuItem>
                </TextField>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <Button variant="contained" sx={primaryBtnSx} onClick={() => scanGate()}>
                    Validate Scan
                  </Button>

                  {!cameraOpen ? (
                    <Button
                      variant="outlined"
                      sx={outlinedBtnSx}
                      onClick={startCamera}
                      disabled={cameraLoading}
                    >
                      {cameraLoading ? "Opening Camera..." : "Scan with Camera"}
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      sx={outlinedBtnSx}
                      onClick={stopCamera}
                    >
                      Stop Camera
                    </Button>
                  )}
                </Stack>

                {cameraOpen && (
                  <Paper elevation={0} sx={{ ...surfaceSx, p: 1.5 }}>
                    <Stack spacing={1}>
                      <Typography sx={{ fontWeight: 800 }}>
                        Camera Scanner
                      </Typography>
                      <Typography sx={{ opacity: 0.8, fontSize: "0.9rem" }}>
                        Hold the QR steadily in front of the camera.
                      </Typography>

                      <Box
                        id={scannerDivId}
                        sx={{
                          width: "100%",
                          minHeight: 320,
                          borderRadius: 2,
                          overflow: "hidden",
                          backgroundColor: "#000",
                          "& video": {
                            width: "100% !important",
                            height: "320px !important",
                            objectFit: "cover",
                            display: "block",
                            borderRadius: "12px",
                          },
                        }}
                      />

                      {!!cameraLoading && (
                        <Typography sx={{ opacity: 0.75 }}>
                          Opening camera...
                        </Typography>
                      )}

                      {!!cameraError && (
                        <Alert severity="error" sx={{ borderRadius: 2 }}>
                          {cameraError}
                        </Alert>
                      )}
                    </Stack>
                  </Paper>
                )}

                {!isSecure && (
                  <Alert severity="warning" sx={{ borderRadius: 2 }}>
                    Camera access works only on HTTPS or localhost.
                  </Alert>
                )}
              </Stack>
            </Paper>

            <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
              <Stack spacing={1.5}>
                <Typography sx={{ fontWeight: 900, color: BLUE }}>
                  3rd Party Integration
                </Typography>

                <Typography sx={{ fontSize: "0.92rem" }}>
                  A 3rd party gate scanner or gate controller should call the public gate API
                  below. It must pass the campus code, gate device code, gate device secret,
                  scan direction, and the QR value present on the student or employee ID card.
                </Typography>

                <Box sx={noteBoxSx}>
                  <Typography sx={{ fontWeight: 800, fontSize: "0.9rem", mb: 0.5 }}>
                    How to call from 3rd party for gate scan
                  </Typography>
                  <Box sx={codeBoxSx}>{thirdPartyCurl}</Box>
                </Box>

                <Box sx={noteBoxSx}>
                  <Typography sx={{ fontWeight: 800, fontSize: "0.9rem", mb: 0.5 }}>
                    Actual request body example
                  </Typography>
                  <Box sx={codeBoxSx}>{thirdPartyExamplePayload}</Box>
                </Box>

                <Box sx={noteBoxSx}>
                  <Typography sx={{ fontWeight: 800, fontSize: "0.9rem", mb: 0.5 }}>
                    Where qrRaw comes from
                  </Typography>
                  <Typography sx={{ fontSize: "0.9rem", mb: 1 }}>
                    The third party should send the <strong>QrValue</strong> stored against the
                    active issued card.
                  </Typography>
                  <Box sx={codeBoxSx}>
{`Select QrValue
From idcard_issued
Where StudentKey = ?????
  AND QrIsActive = 1;`}
                  </Box>
                </Box>

                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  Replace <strong>domain</strong>, <strong>campus code</strong>,{" "}
                  <strong>device code</strong>, <strong>device secret</strong>, and the QR value
                  with actual values. The QR value should be the full value stored in{" "}
                  <strong>idcard_issued.QrValue</strong>.
                </Alert>
              </Stack>
            </Paper>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
};

export default WalletGatePage;